import {
  Box,
  Card,
  Stack,
  Alert,
  Dialog,
  Button,
  Divider,
  IconButton,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import dayjs from 'dayjs';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { ResponsiveDivider } from 'src/components/ResponsiveDivider';
import { useTranslate } from 'src/locales';
import { useCreatePayoutMutation } from 'src/store/slices/paymentsAPISlice';
import { useState, useEffect } from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  balance: string;
  date: string;
  from: string;
  to: string;
  type: 'service' | 'client';
  clientId: string;
};

export function PayoutDialog({
  open,
  onClose,
  onConfirm,
  balance,
  date,
  from,
  to,
  type,
  clientId,
}: Props) {
  const { t } = useTranslate();
  const [createPayout, { isLoading }] = useCreatePayoutMutation();
  const [alertVisible, setAlertVisible] = useState(true);

  useEffect(() => {
    if (open) {
      setAlertVisible(true);
    }
  }, [open]);

  // Common styles for card typography
  const labelStyle = {
    color: 'text.secondary',
    fontWeight: 500,
    fontSize: '18px !important',
    fontFamily: 'Intrum Sans',
    mb: 0.5,
  };

  const valueStyle = {
    color: 'warning.contrastText',
    fontSize: '18px !important',
    fontFamily: 'Inter Variable',
  };

  const handleConfirm = async () => {
    try {
      const response = await createPayout({
        currency: balance.split(' ')[1] || 'EUR',
        clientId,
        type,
        periodEnd: date,
      }).unwrap();

      if (!response.success) {
        throw new Error('Failed to create payout');
      }

      toast.success(
        t(
          response.result.payout.type === 'client'
            ? 'toastMessages.payoutCreatedSuccessful'
            : 'toastMessages.payinCreatedSuccessful'
        ),
        { position: 'bottom-left' }
      );
      onConfirm();
      onClose();
    } catch (error: any) {
      console.error('Error creating payout:', error);

      const errorMessage =
        error?.data?.error || error?.message || t('toastMessages.payoutCreationFailed');
      toast.error(errorMessage, { position: 'bottom-left' });
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-test-id="payout-dialog"
      disableEscapeKeyDown={isLoading}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: { xs: '95vw', sm: '50vw' },
          width: { xs: '95vw', sm: 'auto' },
          margin: { xs: 1, sm: 3 },
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Typography variant="h6" component="div">
            {t(`dialogs.${type}Payout.title`)}
          </Typography>
        </Stack>
      </DialogTitle>

      <Box sx={{ position: 'absolute', right: 16, top: 16 }}>
        <IconButton onClick={onClose} sx={{ p: 1 }}>
          <Iconify icon="mingcute:close-line" width={18} height={18} />
        </IconButton>
      </Box>
      <Divider sx={{ borderStyle: 'dashed', mb: 3 }} />
      <DialogContent sx={{ pb: 2 }}>
        <Stack spacing={3}>
          {/* Balance and Date Card */}
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              backgroundColor: 'white',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="space-around"
              spacing={{ xs: 2, sm: 0 }}
              divider={<ResponsiveDivider />}
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box textAlign={{ xs: 'center', sm: 'left' }}>
                  <Typography variant="subtitle2" sx={labelStyle}>
                    {t('dialogs.clientPayout.balance')}
                  </Typography>
                  <Typography variant="h5" sx={valueStyle}>
                    {balance}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box textAlign={{ xs: 'center', sm: 'left' }}>
                  <Typography variant="subtitle2" sx={labelStyle}>
                    {t('dialogs.clientPayout.date')}
                  </Typography>
                  <Typography variant="h5" sx={valueStyle}>
                    {dayjs(date).format('DD.MM.YYYY')}
                  </Typography>
                </Box>
              </Grid>
            </Stack>
          </Card>

          {/* Alert */}
          {alertVisible && (
            <Alert
              severity="warning"
              icon={
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'rgba(255, 190, 59, 0.08)', // 8% transparent yellow
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ml: 1,
                  }}
                >
                  <Iconify
                    icon="solar:danger-triangle-bold"
                    width={20}
                    height={18}
                    color="warning.main"
                  />
                </Box>
              }
              sx={{
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                p: 0,
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '18px',
                    lineHeight: 1.2,
                  }}
                >
                  {t('dialogs.clientPayout.alertMessage')}
                </Typography>

                <IconButton
                  onClick={() => setAlertVisible(false)}
                  sx={{
                    width: 20,
                    height: 20,
                    border: '1px solid rgba(157, 164, 171, 0.16)', // 16% transparent gray
                    borderRadius: '50%',
                    minWidth: '20px',
                    minHeight: '20px',
                    p: 0,
                    mr: 1,
                  }}
                >
                  <Iconify icon="mingcute:close-line" width={12} height={12} />
                </IconButton>
              </Box>
            </Alert>
          )}

          {/* From and To Card */}
          <Card
            sx={{
              p: 2,
              borderRadius: '16px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              backgroundColor: 'white',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="space-around"
              spacing={{ xs: 2, sm: 0 }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                spacing={2}
                textAlign={{ xs: 'center', sm: 'left' }}
              >
                <Typography variant="subtitle2" sx={{ ...labelStyle, mb: 0 }}>
                  {t('dialogs.clientPayout.from')}
                </Typography>
                <Typography variant="h5" sx={valueStyle}>
                  {from}
                </Typography>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                spacing={2}
                textAlign={{ xs: 'center', sm: 'left' }}
              >
                <Typography variant="subtitle2" sx={{ ...labelStyle, mb: 0 }}>
                  {t('dialogs.clientPayout.to')}
                </Typography>
                <Typography variant="h5" sx={valueStyle}>
                  {to}
                </Typography>
              </Stack>
            </Stack>
          </Card>

          {/* Confirmation Card */}
          <Card
            sx={{
              p: 3,
              borderRadius: '16px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              backgroundColor: 'white',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: 'warning.contrastText',
                fontWeight: 500,
                fontSize: '18px',
                lineHeight: 1.2,
                fontFamily: 'Intrum Sans',
                mb: 2,
              }}
            >
              {t(`dialogs.${type}Payout.confirmationText`, { from, to })}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} pl={2}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: 'info.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: 'info.main',
                    color: 'white',
                    fontSize: 12,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  i
                </Box>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'info.main',
                  fontWeight: 400,
                  fontFamily: 'Intrum Sans',
                }}
              >
                {t('dialogs.clientPayout.infoNote')}
              </Typography>
            </Stack>
          </Card>
        </Stack>
      </DialogContent>

      <Divider sx={{ borderStyle: 'dashed' }} />
      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          data-test-id="payout-dialog-cancel"
          sx={{
            flex: 1,
            fontWeight: 500,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading}
          data-test-id="payout-dialog-confirm"
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
          sx={{
            flex: 1,
            backgroundColor: '#F16437',
            fontWeight: 500,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {isLoading ? t('buttons.processing') : t('buttons.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
